/**
 * Submit File View — the adviser's file submission form.
 *
 * Allows Marinus to submit a new client file with metadata, work category,
 * priority, the Client File Brief (the "Summary" field, stored in
 * files.written_instructions), an optional voice note and attachments.
 *
 * Since 29 September 2026 the brief is written by the adviser on this form:
 * no AI summary is generated and no transcription runs. The voice note is
 * uploaded as-is (an attachment Wouter can play back) and is optional.
 *
 * Supports URL parameters for pre-populated fields from calendar triggers:
 *   ?clientName=J+Smith&appointmentDate=2026-04-01
 */

import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Send, Paperclip, X, FileText, AlertCircle, Loader, Layers, ClipboardList } from 'lucide-react';
import { useAuth } from '../auth/AuthProvider';
import { submitFile as submitFileService } from '../services/pipelineService';
import { uploadAttachments, uploadVoiceNote } from '../api/storageApi';
import { sendNewFileNotification } from '../api/mailApi';
import { createSystemNote as createFileSystemNote } from '../api/fileNotesApi';
import { createSystemNote as createPrepSystemNote } from '../api/prepNotesApi';
import { formatDateForInput, getTodaySASTDateString } from '../utils/dateHelpers';
import { formatPrepYmd } from '../utils/prepRequests';
import { buildClientFileBrief, syncBriefHeader, isBriefUntouched } from '../utils/clientFileBrief';
import { returnToDashboard } from '../utils/returnToDashboard';
import VoiceRecorder from '../components/recorder/VoiceRecorder';
import CommissionSplitEditor, {
  defaultCommissionSplits,
} from '../components/files/CommissionSplitEditor';

// Work origin options for the submission form
const WORK_CATEGORIES = [
  'Ad-Hoc Client Request',
  'Annual Review',
  'New Client Consultation',
];

export default function SubmitFile() {
  const { user, role } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fileInputRef = useRef(null);

  // Pre-populated from calendar trigger URL params
  const [clientName, setClientName] = useState(searchParams.get('clientName') || '');
  // Pre-fill from a completed preparation request ("Create file folder",
  // §26.9): language and priority map cleanly between the two record types,
  // so they arrive as URL params alongside clientName — validated against
  // their allowed values, and fully editable before submission like any
  // other field. `fromPrep` is the prep request's id, stored as the
  // navigational back-link and named in the cross-linking system notes.
  const fromPrepRequestId = searchParams.get('fromPrep') || null;
  const [clientLanguage, setClientLanguage] = useState(() =>
    ['English', 'Afrikaans'].includes(searchParams.get('clientLanguage'))
      ? searchParams.get('clientLanguage')
      : 'English'
  );
  const [appointmentDate, setAppointmentDate] = useState(
    searchParams.get('appointmentDate') || formatDateForInput(new Date())
  );
  const [workCategory, setWorkCategory] = useState(WORK_CATEGORIES[0]);
  // Adviser commission splits — seeded with the canonical 70 / 30 default.
  // The editor exposes its own validity flag; we mirror it into local
  // state so the submit button can be gated on `commissionSplitsValid`.
  const [commissionSplits, setCommissionSplits] = useState(defaultCommissionSplits());
  const [commissionSplitsValid, setCommissionSplitsValid] = useState(true);
  const [priority, setPriority] = useState(() =>
    ['Normal', 'Urgent'].includes(searchParams.get('priority'))
      ? searchParams.get('priority')
      : 'Normal'
  );
  const [attachments, setAttachments] = useState([]);
  // Optional voice note — uploaded as-is for Wouter to play back. Nothing is
  // transcribed or summarised from it.
  const [voiceBlob, setVoiceBlob] = useState(null);
  // The Client File Brief ("Summary" field → files.written_instructions).
  // Pre-populated with the template on open; the Client / Date / Work
  // Category header lines follow the form's fields while the adviser edits
  // the rest. The date is the SAST calendar date, never browser local time.
  const briefDate = formatPrepYmd(getTodaySASTDateString());
  const [brief, setBrief] = useState(() =>
    buildClientFileBrief({
      clientName: searchParams.get('clientName') || '',
      dateStr: briefDate,
      workCategory: WORK_CATEGORIES[0],
    })
  );
  const [briefError, setBriefError] = useState(null);
  useEffect(() => {
    setBrief((prev) => syncBriefHeader(prev, { clientName, dateStr: briefDate, workCategory }));
  }, [clientName, workCategory, briefDate]);
  // Multi-case screenshot gate. Default to a single case so the form
  // mirrors the legacy single-screenshot flow until the adviser bumps
  // the count. Each case carries a free-text description — the
  // adviser fills it in comprehensively at submission time and the
  // new business handover memo reads it back verbatim.
  const [caseCount, setCaseCount] = useState(1);
  const [caseLabels, setCaseLabels] = useState(['']);
  // "Load as test file" — Adviser-only marker (migration 23). Rendered and
  // honoured only when the active role is Adviser; forced false otherwise.
  const [isTest, setIsTest] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [processingStage, setProcessingStage] = useState(null); // Track submission progress
  const [error, setError] = useState(null);

  const handleAddFiles = (e) => {
    const files = Array.from(e.target.files);
    // Validate: max 5 files, max 25MB each
    const valid = files.filter((f) => {
      if (f.size > 25 * 1024 * 1024) {
        setError(`File "${f.name}" exceeds 25MB limit`);
        return false;
      }
      return true;
    });

    const total = attachments.length + valid.length;
    if (total > 5) {
      setError('Maximum 5 attachments allowed');
      return;
    }

    setAttachments((prev) => [...prev, ...valid]);
    setError(null);
  };

  const removeAttachment = (index) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  // Resize the case-descriptions list when the count changes,
  // preserving any text the adviser has already typed. Increasing
  // appends empty strings; decreasing trims from the end.
  const handleCaseCountChange = (raw) => {
    const parsed = parseInt(raw, 10);
    if (Number.isNaN(parsed)) return;
    const clamped = Math.max(1, Math.min(10, parsed));
    setCaseCount(clamped);
    setCaseLabels((prev) => {
      if (clamped === prev.length) return prev;
      if (clamped > prev.length) {
        return [...prev, ...Array(clamped - prev.length).fill('')];
      }
      return prev.slice(0, clamped);
    });
  };

  const handleCaseLabelChange = (index, value) => {
    setCaseLabels((prev) => prev.map((label, i) => (i === index ? value : label)));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!clientName.trim()) {
      setError('Client name is required');
      return;
    }

    // Soft guard: the brief must have been written in — an untouched
    // template (all three "1. xxx" lines still present) is never submitted.
    if (isBriefUntouched(brief)) {
      setBriefError('Complete at least the Instructions section of the brief before submitting.');
      setError('Complete at least the Instructions section of the brief before submitting.');
      return;
    }
    setBriefError(null);

    // Hard gate: the commission split must total 100% with every row named.
    // The submit button is already disabled when this is false, so this is
    // a safety net for keyboard-driven submits.
    if (!commissionSplitsValid) {
      setError('Adviser commission split must total 100% with every row named.');
      return;
    }

    // Cases section: count must be 1..10 and every label must be non-empty
    // after trim. Labels are stored verbatim (trimmed) on the file row.
    if (!Number.isInteger(caseCount) || caseCount < 1 || caseCount > 10) {
      setError('Number of cases must be between 1 and 10.');
      return;
    }
    if (caseLabels.length !== caseCount) {
      setError('Case labels do not match the number of cases — please review.');
      return;
    }
    const trimmedLabels = caseLabels.map((s) => s.trim());
    if (trimmedLabels.some((s) => !s)) {
      setError('Please write a description for every case so Wouter (and the handover memo) knows what each one is.');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      const fileId = crypto.randomUUID();
      const category = workCategory;

      // Defensive: only the Adviser may mark a submission as a test file,
      // regardless of any UI state (the checkbox is only rendered for the
      // Adviser, but the role check is re-applied here as a safety net).
      const isTestSubmission = role === 'adviser' && isTest;
      // Test files carry a "TEST — " name prefix so they are recognisable
      // everywhere, unless the adviser already typed one starting "TEST".
      let submittedClientName = clientName.trim();
      if (isTestSubmission && !submittedClientName.startsWith('TEST')) {
        submittedClientName = `TEST — ${submittedClientName}`;
      }

      // Upload attachments to Supabase Storage
      let attachmentUrls = [];
      if (attachments.length > 0) {
        setProcessingStage('Uploading attachments...');
        attachmentUrls = await uploadAttachments(fileId, attachments);
      }

      // Upload the optional voice note as-is. No transcription and no
      // summary: the brief below carries the written instructions.
      let voiceNoteUrl = null;
      if (voiceBlob) {
        setProcessingStage('Uploading voice note...');
        voiceNoteUrl = await uploadVoiceNote(fileId, voiceBlob);
      }

      setProcessingStage('Creating file record...');

      // New submissions always land in Submitted, regardless of In
      // Progress state. The system never auto-promotes from Submitted
      // (CLAUDE.md §7.6).
      const file = await submitFileService({
        fileData: {
          fileId,
          clientName: submittedClientName,
          appointmentDate,
          submittedBy: user?.email,
          workCategory: category,
          priority,
          clientLanguage,
          attachments: attachmentUrls,
          voiceNoteUrl,
          // The Client File Brief — the adviser's written instructions.
          writtenInstructions: brief.trim(),
          outlookEventId: searchParams.get('eventId') || null,
          // Navigational back-link only (§26.9): no stats, SLA, bonus or
          // deadline logic ever traverses it.
          sourcePrepSheetId: fromPrepRequestId,
          caseCount,
          caseLabels: trimmedLabels,
          // Persist the adviser commission split as a clean array of
          // `{name, percent}` objects so it round-trips cleanly
          // through JSONB. Percent values are coerced to integers in
          // case the editor left a string in place.
          commissionSplits: commissionSplits.map((row) => ({
            name: (row.name || '').trim(),
            percent: parseInt(row.percent, 10) || 0,
          })),
          isTest: isTestSubmission,
        },
      });

      // Cross-linking system notes (§26.9) — one on each record, following
      // the existing system-note conventions. Non-blocking: a note failure
      // never affects the created file. The prep request itself is untouched
      // (still completed, still in its stack).
      if (fromPrepRequestId) {
        try {
          await createFileSystemNote(
            file.id,
            `Created from preparation request for ${submittedClientName}.`.slice(0, 200)
          );
        } catch (noteErr) {
          console.warn('[prep-link] file-side system note failed:', noteErr);
        }
        try {
          await createPrepSystemNote(
            fromPrepRequestId,
            'File folder created from this request.'
          );
        } catch (noteErr) {
          console.warn('[prep-link] prep-side system note failed:', noteErr);
        }
      }

      // Send notification email to Wouter. Test files never notify
      // (CLAUDE.md §33) — mailApi also guards on file.isTest server-side
      // of this call, so the skip holds even if this check is bypassed.
      if (!isTestSubmission) {
        try {
          await sendNewFileNotification(file, 0);
        } catch (mailErr) {
          console.warn('Notification email failed (non-critical):', mailErr);
        }
      }

      // Whole chain resolved (uploads, record, cross-link notes,
      // notification): return to the dashboard with the new card
      // highlighted in Inbox (CLAUDE.md §39). `replace` so browser back
      // does not reopen the completed form. On any failure above we never
      // reach this line — the catch below keeps the user here with the error.
      returnToDashboard(navigate, {
        role,
        cardType: 'file',
        cardId: file.id,
        message: 'File folder submitted',
        replace: true,
      });

    } catch (err) {
      console.error('Submission failed:', err);
      setError(err.message || 'Failed to submit file. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <h2 style={styles.title}>
        <FileText size={20} /> Submit New File
      </h2>

      <motion.form
        className="card"
        style={styles.form}
        onSubmit={handleSubmit}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
      >
        {error && (
          <div style={styles.errorBox}>
            <AlertCircle size={16} />
            <span>{error}</span>
          </div>
        )}

        {/* Client Name */}
        <div style={styles.field}>
          <label style={styles.label}>Client Name *</label>
          <input
            type="text"
            value={clientName}
            onChange={(e) => setClientName(e.target.value)}
            placeholder="e.g. Van der Merwe"
            required
          />
        </div>

        {/* Client Language Preference */}
        <div style={styles.field}>
          <label style={styles.label}>Client Language Preference</label>
          <select value={clientLanguage} onChange={(e) => setClientLanguage(e.target.value)}>
            <option value="English">English</option>
            <option value="Afrikaans">Afrikaans</option>
          </select>
        </div>

        {/* Consultation Date */}
        <div style={styles.field}>
          <label style={styles.label}>Consultation Date</label>
          <input
            type="date"
            value={appointmentDate}
            onChange={(e) => setAppointmentDate(e.target.value)}
          />
        </div>

        {/* Work Originated From */}
        <div style={styles.field}>
          <label style={styles.label}>Work Originated From</label>
          <select value={workCategory} onChange={(e) => setWorkCategory(e.target.value)}>
            {WORK_CATEGORIES.map((cat) => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>

        {/* Adviser commission split. Defaults to 70 / 30 (M Lourens /
            Optimum PFP); the adviser can rebalance, rename, or add rows.
            Submission is blocked unless rows total exactly 100% and every
            row has a name. */}
        <div style={styles.field}>
          <label style={styles.label}>
            Adviser Commission Split
            <span style={styles.hint}> — must total 100%</span>
          </label>
          <CommissionSplitEditor
            value={commissionSplits}
            onChange={setCommissionSplits}
            onValidityChange={setCommissionSplitsValid}
            disabled={submitting}
          />
        </div>

        {/* Priority */}
        <div style={styles.field}>
          <label style={styles.label}>Priority</label>
          <div style={styles.radioGroup}>
            <label style={styles.radioLabel}>
              <input
                type="radio"
                name="priority"
                value="Normal"
                checked={priority === 'Normal'}
                onChange={(e) => setPriority(e.target.value)}
                style={styles.radio}
              />
              <span className="badge badge-normal">Normal</span>
            </label>
            <label style={styles.radioLabel}>
              <input
                type="radio"
                name="priority"
                value="Urgent"
                checked={priority === 'Urgent'}
                onChange={(e) => setPriority(e.target.value)}
                style={styles.radio}
              />
              <span className="badge badge-urgent">Urgent</span>
            </label>
          </div>
        </div>

        {/* Voice Note — optional. Attached to the file as-is for Wouter to
            play back on the file detail view; nothing is transcribed or
            summarised from it. */}
        <div style={styles.field}>
          <label style={styles.label}>
            Voice Note
            <span style={styles.hint}> (optional — attached to the file for Wouter to play back)</span>
          </label>
          <VoiceRecorder
            onRecordingComplete={(blob) => setVoiceBlob(blob)}
            disabled={submitting}
          />
        </div>

        {/* Summary — the Client File Brief (files.written_instructions).
            Pre-populated with the template; the Client / Date / Work
            Category lines follow the fields above. Fully editable; the
            soft guard on submit refuses an untouched template. */}
        <div style={styles.field}>
          <label style={styles.label} htmlFor="client-file-brief">
            <ClipboardList size={14} style={{ verticalAlign: '-2px', marginRight: '0.3rem', color: 'var(--cp-secondary)' }} />
            Summary
            <span style={styles.hint}> — the Client File Brief for Wouter; complete each section</span>
          </label>
          <textarea
            id="client-file-brief"
            value={brief}
            onChange={(e) => {
              setBrief(e.target.value);
              if (briefError) setBriefError(null);
            }}
            rows={16}
            style={styles.briefTextarea}
            disabled={submitting}
            aria-invalid={briefError ? 'true' : undefined}
          />
          {briefError && (
            <p style={styles.fieldError} role="alert">{briefError}</p>
          )}
        </div>

        {/* Cases in this file. Sits between the Summary and
            Attachments. The advisor declares how many separate
            transactions are bundled into this file and writes a free-
            text description for each. The same description doubles as
            the case label alongside the assistant's AtWork screenshot
            slot, and is read back verbatim on the new business
            handover memo PDF — so the adviser fills it in
            comprehensively at submission time. */}
        <div style={styles.field}>
          <label style={styles.label}>
            <Layers size={14} style={{ verticalAlign: '-2px', marginRight: '0.3rem', color: 'var(--cp-secondary)' }} />
            Cases in this file
          </label>
          <p style={{ ...styles.hint, marginTop: 0, marginBottom: '0.6rem' }}>
            How many separate transactions does this file contain? Write a comprehensive description for each — provider, product, transaction type, amount, anything else Vicky needs. This goes straight onto the handover memo for new business.
          </p>
          <div style={styles.caseCountRow}>
            <label style={styles.caseCountLabel} htmlFor="case-count-input">
              Number of cases
            </label>
            <input
              id="case-count-input"
              type="number"
              min={1}
              max={10}
              value={caseCount}
              onChange={(e) => handleCaseCountChange(e.target.value)}
              style={styles.caseCountInput}
              disabled={submitting}
            />
          </div>
          <div style={styles.caseLabelList}>
            {caseLabels.map((label, idx) => (
              <div key={idx} style={styles.caseCard}>
                <div style={styles.caseLabelRow}>
                  <span style={styles.caseLabelTag}>Case {idx + 1}</span>
                  <textarea
                    value={label}
                    onChange={(e) => handleCaseLabelChange(idx, e.target.value)}
                    placeholder={'e.g.\nAllan Gray — Tax-Free Savings Account\nNew investment of R 36 000 (annual lump sum)'}
                    style={styles.caseLabelTextarea}
                    disabled={submitting}
                    rows={3}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Attachments */}
        <div style={styles.field}>
          <label style={styles.label}>
            Attachments
            <span style={styles.hint}> (max 5 files, 25MB each — PDF, DOCX, XLSX, JPG, PNG)</span>
          </label>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".pdf,.docx,.xlsx,.jpg,.jpeg,.png"
            onChange={handleAddFiles}
            style={{ display: 'none' }}
          />
          <button
            type="button"
            className="btn btn-outline"
            onClick={() => fileInputRef.current?.click()}
            style={styles.attachBtn}
          >
            <Paperclip size={14} /> Add Files
          </button>
          {attachments.length > 0 && (
            <div style={styles.fileList}>
              {attachments.map((file, idx) => (
                <div key={idx} style={styles.fileItem}>
                  <FileText size={14} color="#6B7A8D" />
                  <span style={styles.fileName}>{file.name}</span>
                  <span style={styles.fileSize}>
                    {(file.size / 1024 / 1024).toFixed(1)}MB
                  </span>
                  <button
                    type="button"
                    onClick={() => removeAttachment(idx)}
                    style={styles.removeBtn}
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Load as test file — Adviser only, and the LAST field on the form:
            it sits immediately above the submit button (CLAUDE.md §33.2).
            The Administration Assistant and New Business Assistant never
            see this checkbox; the submit handler additionally forces
            isTest to false for non-Advisers. */}
        {role === 'adviser' && (
          <div style={styles.field}>
            <label style={styles.radioLabel}>
              <input
                type="checkbox"
                checked={isTest}
                onChange={(e) => setIsTest(e.target.checked)}
                style={styles.radio}
                disabled={submitting}
              />
              <span style={{ ...styles.label, display: 'inline', marginBottom: 0 }}>Load as test file</span>
            </label>
            <p style={{ ...styles.hint, margin: '0.35rem 0 0' }}>
              Test files are excluded from stats and pipeline rules, and can be deleted later. Visible to Adviser only.
            </p>
          </div>
        )}

        {/* Submit Button. */}
        {(() => {
          const splitsBlocked = !commissionSplitsValid;
          const isDisabled = submitting || splitsBlocked;
          return (
            <motion.button
              type="submit"
              className="btn btn-primary"
              style={styles.submitBtn}
              disabled={isDisabled}
              whileHover={{ scale: isDisabled ? 1 : 1.01 }}
              whileTap={{ scale: isDisabled ? 1 : 0.99 }}
              title={splitsBlocked ? 'Adviser commission split must total 100% with every row named.' : undefined}
            >
              {submitting ? (
                <Loader size={16} style={{ animation: 'spin 1s linear infinite' }} />
              ) : (
                <Send size={16} />
              )}
              {submitting
                ? (processingStage || 'Submitting...')
                : splitsBlocked
                  ? 'Fix commission split to submit'
                  : 'Submit File to Wouter'}
            </motion.button>
          );
        })()}
      </motion.form>
    </div>
  );
}

const styles = {
  title: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    fontSize: '1.3rem',
    fontWeight: 600,
    marginBottom: '1rem',
    color: 'var(--cp-text)',
  },
  form: {
    maxWidth: '640px',
    padding: '2rem',
  },
  field: {
    marginBottom: '1.25rem',
  },
  label: {
    display: 'block',
    fontSize: '0.85rem',
    fontWeight: 600,
    color: 'var(--cp-text)',
    marginBottom: '0.35rem',
  },
  hint: {
    fontWeight: 400,
    color: '#6B7A8D',
    fontSize: '0.8rem',
  },
  radioGroup: {
    display: 'flex',
    gap: '1rem',
  },
  radioLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.35rem',
    cursor: 'pointer',
  },
  radio: {
    width: 'auto',
    cursor: 'pointer',
  },
  placeholder: {
    padding: '1rem',
    background: '#F0F4F8',
    borderRadius: '10px',
    color: '#6B7A8D',
    fontSize: '0.85rem',
    textAlign: 'center',
    border: '1.5px dashed #D0DAE6',
  },
  attachBtn: {
    marginBottom: '0.5rem',
  },
  fileList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.35rem',
  },
  fileItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.4rem 0.6rem',
    background: '#F0F4F8',
    borderRadius: '6px',
    fontSize: '0.8rem',
  },
  fileName: {
    flex: 1,
    color: 'var(--cp-text)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  fileSize: {
    color: '#6B7A8D',
    fontSize: '0.75rem',
    flexShrink: 0,
  },
  removeBtn: {
    background: 'none',
    border: 'none',
    color: '#C0392B',
    padding: '2px',
    cursor: 'pointer',
    display: 'flex',
  },
  textarea: {
    resize: 'vertical',
    minHeight: '80px',
  },
  caseCountRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    marginBottom: '0.75rem',
  },
  caseCountLabel: {
    fontSize: '0.85rem',
    fontWeight: 500,
    color: 'var(--cp-text)',
  },
  caseCountInput: {
    width: '5rem',
    textAlign: 'center',
    fontWeight: 600,
  },
  caseLabelList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
  },
  caseCard: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
    padding: '0.75rem',
    background: '#F7FAFC',
    border: '1px solid #E1E8F0',
    borderRadius: '10px',
  },
  caseLabelRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '0.6rem',
  },
  caseLabelTag: {
    flexShrink: 0,
    minWidth: '4.5rem',
    padding: '0.3rem 0.55rem',
    marginTop: '0.4rem',
    background: 'rgba(27, 154, 170, 0.12)',
    color: '#0F4C75',
    fontSize: '0.75rem',
    fontWeight: 600,
    borderRadius: '999px',
    textAlign: 'center',
    letterSpacing: '0.02em',
  },
  caseLabelTextarea: {
    flex: 1,
    minHeight: '5.25rem',
    resize: 'vertical',
    fontFamily: 'inherit',
    fontSize: '0.9rem',
    lineHeight: 1.45,
    padding: '0.55rem 0.7rem',
  },
  // The Client File Brief textarea: tall enough to show the whole template
  // without scrolling on desktop; the form's own font, with the line
  // height and pre-wrap keeping the numbered sections aligned as typed.
  briefTextarea: {
    resize: 'vertical',
    minHeight: '22rem',
    fontFamily: 'inherit',
    fontSize: '0.9rem',
    lineHeight: 1.5,
    whiteSpace: 'pre-wrap',
  },
  fieldError: {
    margin: '0.4rem 0 0',
    fontSize: '0.8rem',
    color: 'var(--cp-danger)',
  },
  submitBtn: {
    width: '100%',
    padding: '0.85rem',
    fontSize: '0.95rem',
    fontWeight: 600,
    marginTop: '0.5rem',
  },
  errorBox: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.75rem 1rem',
    background: 'rgba(192, 57, 43, 0.08)',
    border: '1px solid rgba(192, 57, 43, 0.2)',
    borderRadius: '8px',
    color: '#C0392B',
    fontSize: '0.85rem',
    marginBottom: '1rem',
  },
};
